/* eslint-disable jsx-a11y/label-has-associated-control */
import { type FC } from 'react';
import InputMask from 'react-input-mask';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import ErrorMessage from '@core/shared/components/ErrorMessage';
import cn from '@core/utils/classNames';

import styles from './PiiForm.module.css';
import type { PiiFormSectionTypes } from './type';
import { validateInputs } from './validateInputs';

const PiiFormSection: FC<PiiFormSectionTypes> = ({
  setEditMode,
  header = 'Your Information',
  piiLeadErrors = [],
  firstName,
  lastName,
  email,
  phone,
  setFirstName,
  setLastName,
  setEmail,
  setPhone,
  onSubmit,
  formErrors,
  setFormErrors,
}) => {
  // flags
  const flags = useFeatureFlags();

  const hasLeadError = (targetField: string) => piiLeadErrors.findIndex(({ field }) => field === targetField) !== -1;
  const leadPhoneError = hasLeadError('phone');

  const hasInputValue = (value: string | undefined | null): boolean =>
    typeof value === 'string' && value.trim().length > 0;

  return (
    <>
      <div className={styles.header}>
        <h5>{header}</h5>
      </div>
      {formErrors?.length !== 0 && (
        <>
          <p className={styles.formErrorText}>
            We were unable to save your information. Please correct the errors listed below.
          </p>
          {formErrors?.map((obj) => (
            <ErrorMessage isFieldGroup={false} message={`Make sure the ${obj.field} field is valid.`} />
          ))}
        </>
      )}

      <form onSubmit={onSubmit}>
        <div className={styles.firstNameLastNameWrapper}>
          <span className={styles.inputWrapper}>
            <input
              className={cn(styles.firstNameInput, hasInputValue(firstName) ? styles.inputHasValue : undefined)}
              id="first-name"
              type="text"
              placeholder=" "
              data-testid="pii-form-first-name-input"
              defaultValue={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
            <label htmlFor="first-name" className={styles.inputLabel}>
              First Name
            </label>
          </span>

          <span className={styles.inputWrapper}>
            <input
              className={cn(styles.lastNameInput, hasInputValue(lastName) ? styles.inputHasValue : undefined)}
              id="last-name"
              type="text"
              placeholder=" "
              data-testid="pii-form-last-name-input"
              defaultValue={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
            <label htmlFor="last-name" className={styles.inputLabel}>
              Last Name
            </label>
          </span>
        </div>

        <span className={styles.inputWrapper}>
          <input
            className={hasInputValue(email) ? styles.inputHasValue : undefined}
            id="email"
            type="email"
            placeholder=" "
            data-testid="pii-form-email-input"
            defaultValue={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <label htmlFor="email" className={styles.inputLabel}>
            Email
          </label>
        </span>
        <span className={styles.inputWrapper}>
          <InputMask
            id="phone"
            className={cn(
              leadPhoneError ? styles.inputError : undefined,
              hasInputValue(phone) ? styles.inputHasValue : undefined
            )}
            mask="(999) 999-9999"
            maskChar=""
            autoComplete="on"
            type="text"
            placeholder=" "
            data-testid="pii-form-phone-input"
            defaultValue={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
          <label htmlFor="phone" className={styles.inputLabel}>
            Phone Number
          </label>
        </span>
        {leadPhoneError && flags?.phoneValidation && (
          <div className={styles.piiLeadError}>
            <ErrorMessage isFieldGroup={false} message="Please provide your valid phone number." />
          </div>
        )}
      </form>
    </>
  );
};

export { PiiFormSection };
